import get from 'lodash/get';
import noop from 'lodash/noop';
import first from 'lodash/first';
import { omitBy } from 'lodash/fp';

import decodeQuestion from '../normalizers/decodeQuestion';
import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  DEPOSIT_SUCCESS,
  GET_ACCESS_TOKEN_STARTED,
  GET_ACCESS_TOKEN_FINISHED,
  INITIALISE_USER,
  RESET_PLAYUP_CAMPAIGN,
  RESET_DEPOSIT_DETAILS,
  RESET_USER_ERRORS,
  RESET_WITHDRAW_DETAILS,
  SET_USER_QUESTION_ANSWER,
  SET_DEPOSIT_LIMITS_AFTERWARDS,
  USER_TOGGLE_CAMPAIGN_MODAL
} from '../constants/actionTypes';
import {
  CLAIM_CREDIT,
  DELETE_PAYMENT_METHOD,
  DEPOSIT,
  FACEBOOK_SIGN_IN,
  LIMITS,
  PASSWORD_RESET,
  PXC_SIGN_IN,
  RAZORPAY_DEPOSIT,
  REQUEST_EMAIL_VERIFICATION,
  REQUEST_PHONE_VERIFICATION,
  SELF_EXCLUDE,
  SMS_LOGIN,
  SMS_VERIFICATION,
  TAKE_A_BREAK,
  TOGGLE_COMMUNICATION_PREFERENCES,
  TRANSACTIONS,
  UPDATE_PHONE,
  UPLOAD_DOCUMENT,
  USER_CREATE_PLAYUP_ACCOUNT,
  USER_BALANCE,
  USER_DETAILS,
  USER_PLAYUP_CAMPAIGN_STATUS,
  USER_QUESTIONS,
  USER_SIGN_IN,
  USER_SIGN_OUT,
  USER_SIGN_UP,
  USER_TERMS,
  USER_UPDATE,
  USER_WITHDRAWAL,
  VERIFY_PHONE,
  WITHDRAWAL_ACCOUNTS,
  PORTAL_SIGN_IN
} from '../constants/apiTypes';
import decodeCurrentUser from '../normalizers/decodeCurrentUser';

const resetState = {
  campaignAction: null,
  campaignMessage: {},
  campaignUsername: null,
  campaignUrl: null,
  campaignProcessing: false,
  campaignProcessed: false,
  showCampaignModal: false,
  campaignIsChecking: false,
  currentUser: {},
  depositErrors: [],
  errors: [],
  facebookSigningIn: false,
  fetchingQuestions: false,
  fetchingUserData: false,
  isClaimingCredit: false,
  isInvalid: false,
  isSignedIn: false,
  isValidating: false,
  login: {},
  makingDeposit: false,
  passwordResetSuccessMessage: null,
  paymentOptions: {},
  phoneCodeCreatedAt: undefined,
  phoneCodeExpiredAt: undefined,
  questions: {},
  requestingEmailVerification: false,
  requestingEmailVerificationErrors: [],
  requestingPhoneVerification: false,
  requestingPhoneVerificationErrors: [],
  resettingPassword: false,
  resettingPasswordErrors: null,
  retrievingTransactions: false,
  retrievingWithdrawalAccounts: false,
  selfExcluded: false,
  selfExcluding: false,
  selfExcludingErrorMessage: '',
  inBreak: false,
  requestingABreak: false,
  requestingABreakErrorMessage: '',
  setLimits: false,
  isSigningIn: false,
  isSigningUp: false,
  token: null,
  transactionErrorMessages: '',
  transactions: [],
  transactionsPagination: {},
  updatingLimits: false,
  updatingLimitsErrorMessage: '',
  updatingUser: false,
  uploadingDocument: false,
  uploadingDocumentErrors: [],
  validationErrors: [],
  verifyingPhone: false,
  verifyingPhoneErrors: [],
  verifyingSMSCode: false,
  withdrawalDetails: null,
  withdrawingWinnings: false
};

const initialState = {
  ...resetState,
  hasInitialised: false
};

function userUpdate(state, action) {
  return {
    hasInitialised: true,
    currentUser: decodeCurrentUser(action.data, action.meta),
    facebookSigningIn: false,
    fetchingUserData: false,
    isInvalid: get(action.payload, 'data.accept_terms') === false,
    isSignedIn: true,
    isSigningIn: false,
    isValidating: false,
    paymentOptions: get(action.data, 'payments', []).reduce(
      (memo, item) => ({
        ...memo,
        [item.id]: {
          label: item.label,
          value: item.id
        }
      }),
      {}
    ),
    token: action.token || state.token,
    updatingUser: false,
    validationErrors: []
  };
}

const apiRequestReducer = switchFunc(
  {
    [CLAIM_CREDIT]: () => ({
      isClaimingCredit: true
    }),
    [DEPOSIT]: () => ({
      depositErrors: [],
      makingDeposit: true
    }),
    [PORTAL_SIGN_IN]: (state, action) => {
      return {
        errors: [],
        isSigningIn: true
      };
    },
    [FACEBOOK_SIGN_IN]: () => ({
      errors: [],
      facebookSigningIn: true
    }),
    [PXC_SIGN_IN]: () => ({
      errors: [],
      isSigningIn: true
    }),
    [LIMITS]: () => ({
      updatingLimits: true,
      updatingLimitsErrorMessage: null
    }),
    [PASSWORD_RESET]: () => ({
      resettingPassword: true
    }),
    [SELF_EXCLUDE]: () => ({
      selfExcluding: true,
      selfExcludingErrorMessage: null
    }),
    [SMS_LOGIN]: () => ({
      errors: [],
      isSigningIn: true
    }),
    [SMS_VERIFICATION]: () => ({
      errors: [],
      verifyingSMSCode: true
    }),
    [TAKE_A_BREAK]: () => ({
      requestingABreak: true,
      requestingABreakErrorMessage: null
    }),
    [UPLOAD_DOCUMENT]: () => ({
      uploadingDocumentErrors: [],
      uploadingDocument: true
    }),
    [USER_DETAILS]: () => ({
      fetchingUserData: true
    }),
    [USER_UPDATE]: () => ({
      updatingUser: true,
      errors: []
    }),
    [USER_SIGN_IN]: () => ({
      errors: [],
      isSigningIn: true
    }),
    [USER_SIGN_UP]: () => ({
      errors: [],
      isSigningUp: true
    }),
    [USER_PLAYUP_CAMPAIGN_STATUS]: () => ({
      campaignIsChecking: true
    }),
    [USER_CREATE_PLAYUP_ACCOUNT]: () => ({
      campaignMessage: {},
      campaignProcessing: true,
      campaignProcessed: false
    }),
    [USER_QUESTIONS]: () => ({
      fetchingQuestions: true
    }),
    [USER_TERMS]: () => ({
      isValidating: true
    }),
    [USER_WITHDRAWAL]: () => ({
      withdrawingWinnings: true,
      withdrawalErrors: []
    }),
    [TRANSACTIONS]: () => ({
      retrievingTransactions: true
    }),
    [WITHDRAWAL_ACCOUNTS]: () => ({
      retrievingWithdrawalAccounts: true
    }),
    [RAZORPAY_DEPOSIT]: () => ({
      depositErrors: [],
      makingDeposit: true
    }),
    [REQUEST_PHONE_VERIFICATION]: () => ({
      requestingPhoneVerification: true,
      requestingPhoneVerificationErrors: []
    }),
    [REQUEST_EMAIL_VERIFICATION]: () => ({
      requestingEmailVerification: true,
      requestingEmailVerificationErrors: []
    }),
    [VERIFY_PHONE]: () => ({
      verifyingPhone: true,
      verifyingPhoneErrors: []
    }),
    [UPDATE_PHONE]: () => ({
      updatingUser: true
    })
  },
  () => noop
);

const apiSuccessReducer = switchFunc(
  {
    [CLAIM_CREDIT]: () => ({
      isClaimingCredit: false
    }),
    [DEPOSIT]: (state, action) => ({
      makingDeposit: false,
      paymentOptions: {
        ...state.paymentOptions,
        [action.data.id]: {
          label: action.data.label,
          value: action.data.id
        }
      }
    }),
    [RAZORPAY_DEPOSIT]: () => ({
      makingDeposit: false
    }),
    [DELETE_PAYMENT_METHOD]: (state, action) => ({
      paymentOptions: omitBy(opt => opt.value === action.id)(state.paymentOptions)
    }),
    [FACEBOOK_SIGN_IN]: userUpdate,
    [PXC_SIGN_IN]: userUpdate,
    [PORTAL_SIGN_IN]: (state, action) => {
      return userUpdate(state, action);
    },
    [USER_PLAYUP_CAMPAIGN_STATUS]: (state, action) => ({
      campaignAction: get(action, 'data.action'),
      campaignIsChecking: false
    }),
    [USER_CREATE_PLAYUP_ACCOUNT]: (state, action) => ({
      campaignAction: get(action, 'data.action'),
      campaignMessage: get(action, 'data.text'),
      campaignUsername: get(action, 'data.username'),
      campaignUrl: get(action, 'data.url'),
      campaignProcessing: false,
      campaignProcessed: true
    }),
    [USER_BALANCE]: userUpdate,
    [USER_DETAILS]: userUpdate,
    [USER_SIGN_IN]: userUpdate,
    [USER_SIGN_UP]: userUpdate,
    [USER_TERMS]: userUpdate,
    [USER_UPDATE]: userUpdate,
    [PASSWORD_RESET]: (state, action) => ({
      passwordResetSuccessMessage: action.data.message,
      resettingPassword: false,
      resettingPasswordErrors: null
    }),
    [SMS_LOGIN]: () => ({
      errors: [],
      isSignedIn: true,
      isSigningIn: false
    }),
    [SMS_VERIFICATION]: () => ({
      errors: [],
      verifyingSMSCode: false
    }),
    [UPLOAD_DOCUMENT]: (state, action) => ({
      uploadingDocument: false,
      withdrawalDetails: action.data
    }),
    [USER_SIGN_OUT]: () => resetState,
    [USER_QUESTIONS]: (state, action) => ({
      fetchingQuestions: false,
      questions: (action.data || []).reduce(
        (memo, item) => ({
          ...memo,
          [item.id]: decodeQuestion(item)
        }),
        {}
      )
    }),
    [USER_WITHDRAWAL]: (state, action) => ({
      withdrawalDetails: action.data,
      withdrawalAmount: action.amount,
      withdrawingWinnings: false
    }),
    [LIMITS]: (state, action) => ({
      currentUser: {
        ...state.currentUser,
        limit: action.data
      },
      updatingLimits: false,
      updatingLimitsErrorMessage: null
    }),
    [TOGGLE_COMMUNICATION_PREFERENCES]: state => ({
      currentUser: {
        ...state.currentUser,
        sms_opted_out: !state.currentUser.sms_opted_out
      }
    }),
    [SELF_EXCLUDE]: state => ({
      currentUser: {
        ...state.currentUser,
        selfExcluded: true
      },
      selfExcluding: false,
      selfExcludingErrorMessage: null
    }),
    [TAKE_A_BREAK]: state => ({
      currentUser: {
        ...state.currentUser,
        inBreak: true
      },
      requestingABreak: false,
      requestingABreakErrorMessage: null
    }),
    [TRANSACTIONS]: (state, action) => ({
      retrievingTransactions: false,
      transactions: action.data,
      transactionsPagination: action.meta.pagination
    }),
    [WITHDRAWAL_ACCOUNTS]: (state, action) => ({
      retrievingWithdrawalAccounts: false,
      withdrawalDetails: first(action.data)
    }),
    [REQUEST_EMAIL_VERIFICATION]: () => ({
      requestingEmailVerification: false
    }),
    [REQUEST_PHONE_VERIFICATION]: (state, action) => ({
      requestingPhoneVerification: false,
      phoneCodeExpiredAt: action.data.expired_at,
      phoneCodeCreatedAt: new Date().toISOString()
    }),
    [VERIFY_PHONE]: state => ({
      verifyingPhone: false,
      currentUser: {
        ...state.currentUser,
        phone_verified: true
      }
    }),
    [UPDATE_PHONE]: (state, action) => ({
      currentUser: {
        ...state.currentUser,
        phone: action.data.phone
      },
      updatingUser: false
    })
  },
  () => state => state
);

const apiFailureReducer = switchFunc(
  {
    [CLAIM_CREDIT]: () => ({
      isClaimingCredit: false
    }),
    [DEPOSIT]: (state, action) => ({
      depositErrors: get(action, 'errors.messages', []),
      makingDeposit: false
    }),
    [RAZORPAY_DEPOSIT]: (state, action) => ({
      depositErrors: get(action, 'errors.messages', []),
      makingDeposit: false
    }),
    [PORTAL_SIGN_IN]: (state, action) => {
      return {
        errors: get(action, 'errors.messages', state.errors),
        isSigningIn: false,
        isSignedIn: false
      };
    },
    [FACEBOOK_SIGN_IN]: (state, action) => ({
      errors: get(action, 'errors.messages', state.errors),
      facebookSigningIn: false
    }),
    [PXC_SIGN_IN]: (state, action) => ({
      errors: get(action, 'errors.messages', state.errors),
      isSigningIn: false,
      isSignedIn: false
    }),
    [LIMITS]: (state, action) => ({
      updatingLimits: false,
      updatingLimitsErrorMessage: get(action, 'errors.messages[0].message')
    }),
    [PASSWORD_RESET]: (state, action) => ({
      resettingPassword: false,
      resettingPasswordErrors: get(action, 'errors.message') || get(action, 'errors.messages'),
      passwordResetSuccessMessage: null
    }),
    [REQUEST_EMAIL_VERIFICATION]: (state, action) => ({
      requestingEmailVerification: false,
      requestingEmailVerificationErrors: get(action, 'errors.messages', [])
    }),
    [REQUEST_PHONE_VERIFICATION]: (state, action) => ({
      requestingPhoneVerification: false,
      requestingPhoneVerificationErrors: get(action, 'errors.messages', [])
    }),
    [SELF_EXCLUDE]: (state, action) => ({
      selfExcluding: false,
      selfExcludingErrorMessage: get(action, 'errors.message') || get(action, 'errors.messages')
    }),
    [TAKE_A_BREAK]: (state, action) => ({
      requestingABreak: false,
      requestingABreakErrorMessage: get(action, 'errors.message') || get(action, 'errors.messages')
    }),
    [TRANSACTIONS]: () => ({
      retrievingTransactions: false
    }),
    [SMS_LOGIN]: (state, action) => ({
      errors: get(action, 'errors.messages', state.errors),
      isSignedIn: false,
      isSigningIn: false
    }),
    [SMS_VERIFICATION]: (state, action) => ({
      errors: get(action, 'errors.messages', state.errors),
      verifyingSMSCode: false
    }),
    [UPDATE_PHONE]: (state, action) => ({
      errors: get(action, 'errors.messages', []),
      updatingUser: false
    }),
    [UPLOAD_DOCUMENT]: (state, action) => ({
      uploadingDocument: false,
      uploadingDocumentErrors: get(action, 'errors.messages', [])
    }),
    [USER_PLAYUP_CAMPAIGN_STATUS]: () => ({
      campaignIsChecking: false
    }),
    [USER_CREATE_PLAYUP_ACCOUNT]: () => ({
      campaignProcessing: false,
      campaignProcessed: false
    }),
    [USER_DETAILS]: () => ({
      hasInitialised: false,
      fetchingUserData: false
    }),
    [USER_SIGN_IN]: (state, action) => ({
      errors: get(action, 'errors.messages', state.errors),
      isSignedIn: false,
      isSigningIn: false
    }),
    [USER_SIGN_UP]: (state, action) => ({
      isSigningUp: false,
      errors: get(action, 'errors.messages', state.errors)
    }),
    [USER_UPDATE]: (state, action) => ({
      updatingUser: false,
      errors: get(action, 'errors.messages', state.errors)
    }),
    [USER_WITHDRAWAL]: (state, action) => ({
      withdrawalErrors: get(action, 'errors.messages', []),
      withdrawingWinnings: false
    }),
    [VERIFY_PHONE]: (state, action) => ({
      verifyingPhone: false,
      verifyingPhoneErrors: get(action, 'errors.messages', [])
    })
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequestReducer(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccessReducer(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => ({
    validationErrors: get(action.meta, 'terms')
      ? [
          {
            key: 'terms',
            message: get(action.meta, 'terms')
          }
        ]
      : [],
    ...apiFailureReducer(action.api)(state, action)
  }),
  [GET_ACCESS_TOKEN_STARTED]: () => ({
    isSigningIn: true
  }),
  [GET_ACCESS_TOKEN_FINISHED]: () => ({
    isSigningIn: false
  }),
  [SET_USER_QUESTION_ANSWER]: (state, action) => ({
    questions: Object.assign({}, state.questions, {
      [action.key]: Object.assign({}, state.questions[action.key], {
        value: action.value
      })
    })
  }),
  [SET_DEPOSIT_LIMITS_AFTERWARDS]: ({ setLimits }) => ({
    setLimits: !setLimits
  }),
  [INITIALISE_USER]: () => ({
    hasInitialised: true
  }),
  [DEPOSIT_SUCCESS]: (state, { amount, paymentType }) => ({
    amount,
    paymentType
  }),
  [USER_TOGGLE_CAMPAIGN_MODAL]: (state, action) => ({
    showCampaignModal:
      action.showCampaignModal !== 'undefined' ? action.showCampaignModal : !state.showCampaignModal
  }),
  [RESET_DEPOSIT_DETAILS]: () => ({
    depositErrors: [],
    makingDeposit: false
  }),
  [RESET_WITHDRAW_DETAILS]: () => ({
    withdrawalAmount: null,
    withdrawalDetails: null,
    withdrawalErrors: []
  }),
  [RESET_PLAYUP_CAMPAIGN]: () => ({
    campaignAction: null
  }),
  [RESET_USER_ERRORS]: () => ({
    errors: [],
    passwordResetSuccessMessage: null,
    selfExcludingErrorMessage: null,
    requestingABreakErrorMessage: null
  })
});
